import { z } from "zod";

export const ASSISTANT_ATTACHMENT_MAX_FILES_PER_MESSAGE = 5;
export const ASSISTANT_ATTACHMENT_MAX_FILES_PER_CONVERSATION = 20;
export const ASSISTANT_ATTACHMENT_MAX_FILE_BYTES = 15 * 1024 * 1024;
export const ASSISTANT_ATTACHMENT_MAX_MESSAGE_BYTES = 45 * 1024 * 1024;
// Each follow-up turn re-downloads and hashes every file a conversation retains, whole, to cut its kept pages.
// This bounds that work per turn. A conversation filled to its token limit with typical 20-page scanned
// pieces of about 2 MB retains about 140 MB, so this refuses only unusually heavy conversations.
export const ASSISTANT_RETAINED_RELOAD_MAX_BYTES = 256 * 1024 * 1024;
export const ASSISTANT_ATTACHMENT_MAX_PDF_PAGES = 50;
export const ASSISTANT_ATTACHMENT_MAX_SPREADSHEET_ROWS_PER_SHEET = 10_000;

export const AssistantAttachmentSourceSchema = z
  .object({
    sourceId: z.string().uuid(),
    ordinal: z
      .number()
      .int()
      .min(0)
      .max(ASSISTANT_ATTACHMENT_MAX_FILES_PER_MESSAGE - 1),
    fileName: z.string().min(1).max(255),
    sizeBytes: z.number().int().positive().max(ASSISTANT_ATTACHMENT_MAX_FILE_BYTES),
  })
  .strict();

export const AssistantAttachmentEnvelopeSchema = z
  .object({
    draftId: z.string().uuid(),
    expectedConversationId: z.string().min(1).max(255).nullable(),
    sources: z.array(AssistantAttachmentSourceSchema).min(1).max(ASSISTANT_ATTACHMENT_MAX_FILES_PER_MESSAGE),
    submissionDigest: z.string().regex(/^[0-9a-f]{64}$/),
  })
  .strict()
  .superRefine((value, context) => {
    const ordinals = new Set(value.sources.map(source => source.ordinal));
    const sourceIds = new Set(value.sources.map(source => source.sourceId));
    if (ordinals.size !== value.sources.length || sourceIds.size !== value.sources.length) {
      context.addIssue({ code: z.ZodIssueCode.custom, message: "Attachment sources and ordinals must be unique." });
    }
    if (value.sources.some((source, index) => source.ordinal !== index)) {
      context.addIssue({ code: z.ZodIssueCode.custom, message: "Attachment ordinals must preserve source order." });
    }
    const totalSizeBytes = value.sources.reduce((total, source) => total + source.sizeBytes, 0);
    if (totalSizeBytes > ASSISTANT_ATTACHMENT_MAX_MESSAGE_BYTES) {
      context.addIssue({ code: z.ZodIssueCode.custom, message: "Attachments exceed the 45 MiB message limit." });
    }
  });

export type AssistantAttachmentEnvelope = z.infer<typeof AssistantAttachmentEnvelopeSchema>;
export type AssistantAttachmentSource = z.infer<typeof AssistantAttachmentSourceSchema>;

export const stableAssistantAttachmentDigestPayload = ({
  turnId,
  message,
  expectedConversationId,
  sources,
}: {
  turnId: string;
  message: string;
  expectedConversationId: string | null;
  sources: AssistantAttachmentSource[];
}): string =>
  JSON.stringify({
    turnId,
    message,
    expectedConversationId,
    sources: [...sources].sort((left, right) => left.ordinal - right.ordinal),
  });

export const ASSISTANT_ATTACHMENT_ACCEPT =
  ".pdf,.png,.jpg,.jpeg,.webp,.csv,.xlsx,.txt,.md," +
  "text/plain,text/markdown,text/csv,application/pdf,image/png,image/jpeg,image/webp," +
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
